import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Navigation } from "@/components/Navigation";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="container mx-auto px-6 py-12 flex-1">
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-4xl font-bold text-center">Privacy Policy</CardTitle>
            <div className="text-center text-muted-foreground space-y-1">
              <p><strong>Effective Date:</strong> 17 of September 2025</p>
              <p><strong>Last Updated:</strong> 17 of September 2025</p>
            </div>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
              <p>
                Welcome to <strong>HiStage</strong> — the world's biggest digital stage for creators and fans.
              </p>
              <p>
                We respect your privacy and are committed to protecting your personal information.
                This Privacy Policy explains how we <strong>collect, use, store, and share</strong> your data when you use our platform, apps, or services ("HiStage", "we", "our", or "us").
              </p>
              <p>
                By accessing or using HiStage, you agree to this Privacy Policy.
                If you do not agree, please discontinue use of the platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">2. What We Collect</h2>
              <p>
                We collect the following categories of information to operate HiStage effectively and personalize your experience:
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">A. Account Information</h3>
              <ul>
                <li>Name, username, password, email address, phone number</li>
                <li>Age and country (for eligibility & regional settings)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">B. Creator Data</h3>
              <ul>
                <li>Stream details (title, tags, performance history)</li>
                <li>Uploaded content (videos, thumbnails, metadata)</li>
                <li>Earnings, payouts, and wallet balances</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">C. Fan Data</h3>
              <ul>
                <li>Profile info, likes, votes, tipping history, and subscription status</li>
                <li>Interactions (comments, messages, reactions during live shows)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">D. Payment Information</h3>
              <ul>
                <li>Processed securely via third-party gateways (Stripe, PayPal, Payoneer, etc.)</li>
                <li>We <strong>do not store</strong> full credit/debit card numbers</li>
                <li>Transaction records and payout history may be retained for auditing</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">E. Technical & Usage Data</h3>
              <ul>
                <li>Device info (model, OS, browser)</li>
                <li>IP address, location (approximate), and session activity</li>
                <li>Cookies, cache, and analytics data (how you use the platform)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ol>
                <li>
                  <strong>Provide the Platform</strong>
                  <ul>
                    <li>Enable live streaming, fan voting, tipping, and payouts</li>
                  </ul>
                </li>
                <li>
                  <strong>Personalize Experiences</strong>
                  <ul>
                    <li>Recommend creators, events, or live sessions you might like</li>
                  </ul>
                </li>
                <li>
                  <strong>Ensure Safety & Compliance</strong>
                  <ul>
                    <li>Detect fraud, protect minors, and enforce Terms of Use</li>
                  </ul>
                </li>
                <li>
                  <strong>Process Payments</strong>
                  <ul>
                    <li>Handle purchases, tips, and creator payouts securely</li>
                  </ul>
                </li>
                <li>
                  <strong>Improve the Platform</strong>
                  <ul>
                    <li>Analyze trends, fix bugs, and develop new features</li>
                  </ul>
                </li>
                <li>
                  <strong>Communicate Updates</strong>
                  <ul>
                    <li>Send service alerts, performance insights, or new feature announcements</li>
                  </ul>
                </li>
              </ol>
              <p>
                We <strong>never sell your personal data</strong> to third parties.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">4. How We Share Data</h2>
              <p>We may share limited data with:</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">🔹 Service Providers</h3>
              <p>Trusted third-party partners for:</p>
              <ul>
                <li>Payment processing (e.g., Stripe, Payoneer)</li>
                <li>Cloud storage (e.g., AWS, Google Cloud)</li>
                <li>Analytics (e.g., Mixpanel, Firebase)</li>
                <li>Messaging and support tools</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">🔹 Legal Authorities</h3>
              <p>
                When required by law or to protect HiStage, users, or the public from harm or fraud.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">🔹 Collaborators & Brands</h3>
              <p>
                With your consent, HiStage may share limited performance analytics with brand partners (for sponsorships or collaborations).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">5. Cookies & Tracking Technologies</h2>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Keep you logged in</li>
                <li>Analyze engagement and performance</li>
                <li>Personalize recommendations</li>
              </ul>
              <p>
                You can adjust cookie settings in your browser at any time.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">6. Data Retention</h2>
              <p>We retain user information <strong>only as long as necessary</strong> for:</p>
              <ul>
                <li>Providing services</li>
                <li>Complying with legal obligations</li>
                <li>Resolving disputes</li>
              </ul>
              <p>
                When you delete your account, most personal data is permanently removed within <strong>30 days</strong>, except where retention is required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">7. User Rights</h2>
              <p>Depending on your location, you have the right to:</p>
              <ul>
                <li><strong>Access</strong> your personal data</li>
                <li><strong>Correct</strong> inaccurate information</li>
                <li><strong>Delete</strong> your account and data ("Right to be Forgotten")</li>
                <li><strong>Withdraw consent</strong> for marketing or analytics</li>
                <li><strong>Export your data</strong> in portable format</li>
              </ul>
              <p>
                You can manage these settings under <strong>Account → Privacy Settings</strong> or email <a href="mailto:privacy@histage.com">privacy@histage.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">8. Children's Privacy</h2>
              <p>
                HiStage is <strong>not intended for children under 16</strong>.
                If we discover an account registered by a minor without consent, it will be suspended and deleted.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">9. Security Measures</h2>
              <p>We use:</p>
              <ul>
                <li><strong>SSL encryption</strong> for all data transmission</li>
                <li><strong>Tokenized payments</strong> (no card storage)</li>
                <li><strong>Role-based data access</strong> (staff access on need-to-know basis)</li>
                <li><strong>Regular audits and penetration testing</strong></li>
              </ul>
              <p>
                While we work hard to protect your data, no system is 100% secure.
                Use strong passwords and avoid sharing your login details.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">10. International Data Transfers</h2>
              <p>
                HiStage operates globally.
                Your data may be transferred and processed in countries outside your own.
                We ensure all transfers comply with <strong>GDPR and international data protection laws</strong> using standard contractual clauses.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy occasionally.
                If we make major changes, we'll notify you via email or in-app notice.
                Continued use of HiStage after updates means you accept the revised policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">12. Contact Us</h2>
              <p>For questions or data requests, reach out to:</p>
              <p>
                📧 <a href="mailto:privacy@histage.com">privacy@histage.com</a><br />
                🌐 <a href="https://www.histage.com/privacy">www.histage.com/privacy</a>
              </p>
            </section>

            <section className="mb-8 p-6 bg-muted rounded-lg">
              <h2 className="text-2xl font-bold mb-4">⚡ TL;DR (Plain Summary)</h2>
              <ul className="space-y-2">
                <li>✓ We collect only what's needed to run HiStage smoothly.</li>
                <li>✓ We don't sell your data.</li>
                <li>✓ You control your privacy and can delete your data anytime.</li>
                <li>✓ Payments are handled by trusted partners.</li>
                <li>✓ Security and transparency are our top priorities.</li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Privacy;
